import { api, RouterInput, RouterOutput } from "../../services/rpc/RpcProvider";
import { useToast } from "@introist/react-foundation/v2";
import { useCallback } from "react";

export type FormPage = RouterOutput["forms"]["v2"]["pages"]["list"][0];

export type FormPageCreate = RouterInput["forms"]["v2"]["pages"]["create"];
export type FormPageUpdate = RouterInput["forms"]["v2"]["pages"]["update"];
export type FormPageSplit = RouterInput["forms"]["v2"]["pages"]["split"];

export const useFormPages = (formId: string, options?: any) => {
  const { data: formPages } = api.forms.v2.pages.list.useQuery({ formId }, options);

  return { formPages };
};

export const useFormPageActions = () => {
  const toast = useToast();
  const apiUtils = api.useContext();

  const listOp = apiUtils.forms.v2.pages.list;
  const fieldsListOp = apiUtils.forms.v2.fields.list;

  const createOp = api.forms.v2.pages.create.useMutation();
  const updateOp = api.forms.v2.pages.update.useMutation();
  const deleteOp = api.forms.v2.pages.delete.useMutation();
  const reorderOp = api.forms.v2.pages.reorder.useMutation();
  const splitOp = api.forms.v2.pages.split.useMutation();

  const create = useCallback(
    async (req: FormPageCreate) => {
      return createOp
        .mutateAsync(req)
        .then(async created => {
          await listOp.refetch({ formId: req.formId });
          toast.success("Page created");
          return created;
        })
        .catch(e => {
          toast.error("Failed to create Page");
          throw e;
        });
    },
    [createOp, listOp, toast]
  );

  const update = useCallback(
    async (req: FormPageUpdate) => {
      return updateOp
        .mutateAsync(req)
        .then(async updated => {
          await listOp.refetch();
          toast.success("Page updated");
          return updated;
        })
        .catch(e => {
          toast.error("Failed to update Page");
          throw e;
        });
    },
    [updateOp, listOp, toast]
  );

  const remove = useCallback(
    async (id: string, formId: string) => {
      await deleteOp
        .mutateAsync({ id })
        .then(async () => {
          await listOp.refetch({ formId });
          toast.success("Page deleted");
        })
        .catch(() => {
          toast.error("Failed to delete Page");
        });
    },
    [deleteOp, toast, listOp]
  );

  const reorder = useCallback(
    async (formId: string, ordering: string[]) => {
      await reorderOp
        .mutateAsync({ formId, ordering })
        .then(async () => {
          await listOp.refetch({ formId });
        })
        .catch(() => {
          toast.error("Failed to save Page ordering");
        });
    },
    [reorderOp, toast, listOp]
  );

  const split = useCallback(
    async (req: FormPageSplit) => {
      return splitOp
        .mutateAsync(req)
        .then(async created => {
          await Promise.all([listOp.refetch({ formId: req.formId }), fieldsListOp.refetch({ formId: req.formId })]);
          toast.success("Form split into pages");
          return created;
        })
        .catch(e => {
          toast.error("Failed to split form into pages");
          throw e;
        });
    },
    [splitOp, toast, listOp, fieldsListOp]
  );

  return { create, update, delete: remove, reorder, split };
};
